import type {
  Recommendation,
  RecommendationCreateVariables,
  RecommendationUpdateProps,
  RecommendationUpdateVariables,
  UseGetRecommendationInterventionsProps,
} from "./recommendation.type";

import { useGetInterventions } from "@/entities/interventions";
import { useCreate, useUpdate } from "@/hooks/queries";

const invalidateKeys = [["observations"]];

export const useRecommendationCreate = (interventionId: number) => {
  const { mutate, mutateAsync, ...queryResult } = useCreate<
    Recommendation,
    RecommendationCreateVariables
  >("recommendations", { invalidateKeys, rootId: interventionId });

  const createRecommendation = (observationId: number) => {
    mutate({ observation_id: observationId });
  };

  const createRecommendationAsync = async (observationId: number) => {
    const response = await mutateAsync({ observation_id: observationId });
    const newRecommendation = response.data.data;
    return newRecommendation;
  };

  return {
    ...queryResult,
    createRecommendation,
    createRecommendationAsync,
  };
};

export const useRecommendationUpdate = ({
  recommendationId,
  interventionId,
}: RecommendationUpdateProps) => {
  const { mutate, mutateAsync, ...queryResult } = useUpdate<
    Recommendation,
    RecommendationUpdateVariables
  >("recommendations", recommendationId, {
    rootId: interventionId,
    invalidateKeys,
  });

  const updateRecommendation = (variables: RecommendationUpdateVariables) => {
    mutate(variables);
  };

  const updateRecommendationAsync = async (
    variables: RecommendationUpdateVariables
  ) => {
    const response = await mutateAsync(variables);
    const newRecommendation = response.data.data;
    return newRecommendation;
  };

  return {
    ...queryResult,
    updateRecommendation,
    updateRecommendationAsync,
  };
};

export const useGetRecommendationInterventions = ({
  recommendationId,
  enabled,
}: UseGetRecommendationInterventionsProps) => {
  const { interventions, ...queryResult } = useGetInterventions({
    params: {
      "filter[recommendation_id]": `${recommendationId}`,
      "page[size]": 100,
    },
    enabled,
  });

  return {
    recommendedInterventions: interventions,
    ...queryResult,
  };
};
